/**
 * The six steps of the create-a-report wizard, and the rule for which of them an
 * operator is allowed to be standing on.
 *
 * 🔑 EACH STEP IS ITS OWN URL, so the browser's back and forward buttons move
 * between them and a step can be reloaded, bookmarked or re-opened. That makes
 * the address bar an input the wizard does not control: anyone can type
 * `/admin/obs-reports/new/charts` before a workbook exists. Reachability is
 * therefore derived from the draft rather than from how the operator got here,
 * and an unreachable step redirects to the furthest one that IS reachable
 * instead of rendering a screen with nothing behind it.
 */

export type WizardStep =
  | "identity"
  | "file"
  | "mapping"
  | "units"
  | "charts"
  | "publish";

/**
 * ⚠️ THE CHART STEP COMES AFTER THE UPLOAD, NOT BESIDE THE TITLE. Which charts a
 * report can draw depends on its parsed figures — a treemap needs three groups, a
 * grouped bar needs two measures that are actually reported — so the report is
 * created and its data uploaded first, and only then can the picker offer
 * anything truthful.
 */
export const STEPS: { id: WizardStep; label: string }[] = [
  { id: "identity", label: "التعريف" },
  { id: "file", label: "الملف" },
  { id: "mapping", label: "الأعمدة" },
  { id: "units", label: "الوحدات" },
  { id: "charts", label: "الرسوم" },
  { id: "publish", label: "المعاينة والنشر" },
];

export const FIRST_STEP: WizardStep = "identity";

export function isWizardStep(value: unknown): value is WizardStep {
  return STEPS.some((s) => s.id === value);
}

export function stepIndex(step: WizardStep): number {
  return STEPS.findIndex((s) => s.id === step);
}

/**
 * What each step needs to exist before it can say anything true.
 *
 * Deliberately NOT "which step did they last complete": a draft restored from
 * storage has no history, and after a reload the only honest answer is what the
 * data itself supports.
 */
export interface StepGates {
  /**
   * A valid AND UNUSED slug, plus an Arabic title — everything else hangs off
   * the slug, and a slug another report already owns cannot be created at all.
   */
  identityDone: boolean;
  /** A workbook has been read and its columns proposed. */
  hasProfile: boolean;
  /** The operator confirmed the column mapping, measures included. */
  mappingDone: boolean;
  /** A workbook has been parsed and stored, so the report has figures. */
  hasData: boolean;
}

export function isStepReachable(step: WizardStep, gates: StepGates): boolean {
  switch (step) {
    case "identity":
      return true;
    case "file":
      return gates.identityDone;
    case "mapping":
      return gates.identityDone && gates.hasProfile;
    case "units":
      return gates.identityDone && gates.hasProfile && gates.mappingDone;
    case "charts":
    case "publish":
      return gates.hasData;
  }
}

/** The furthest step the draft can currently support. */
export function lastReachableStep(gates: StepGates): WizardStep {
  let furthest: WizardStep = FIRST_STEP;
  for (const { id } of STEPS) {
    if (isStepReachable(id, gates)) furthest = id;
  }
  return furthest;
}

/** Where to send someone who asked for a step the draft cannot support. */
export function resolveStep(
  requested: WizardStep,
  gates: StepGates,
): WizardStep {
  return isStepReachable(requested, gates) ? requested : lastReachableStep(gates);
}
